import { Injectable, inject } from '@angular/core';
import { FirebaseService } from './firebase.service';
import { httpsCallable } from 'firebase/functions';

export interface CampaignInfo {
  id: string;
  name: string;
  status: string;
  startDate: string;
  endDate: string;
  shortCode: string;
  clientId: string;
  clientName: string;
}

export interface CampaignValidationResult {
  isValid: boolean;
  status: 'valid' | 'missing_short_code' | 'invalid_short_code' | 'inactive' | 'expired' | 'not_started' | 'error';
  message: string;
  campaign?: CampaignInfo;
  shortCode?: string;
  redirectUrl?: string;
  suggestedActions?: string[];
}

export interface ValidationResponse {
  isValid: boolean;
  reason?: string;
  campaign?: CampaignInfo;
  startDate?: string;
  endDate?: string;
}

@Injectable({
  providedIn: 'root'
})
export class CampaignValidationService {
  private firebaseService = inject(FirebaseService);

  async validateCampaign(shortCode: string | null): Promise<CampaignValidationResult> {
    // Handle missing short code
    if (!shortCode) {
      return {
        isValid: false,
        status: 'missing_short_code',
        message: 'No campaign code was provided in the link.',
        suggestedActions: [
          'Check that you scanned the QR code correctly',
          'Ask for a new link from the venue',
          'Contact support if the problem persists'
        ]
      };
    }

    try {
      // Call the campaign validation function
      const validateCampaign = httpsCallable(this.firebaseService.functions, 'validateCampaign');
      const result = await validateCampaign({ shortCode });
      const validation = result.data as ValidationResponse;

      if (validation.isValid) {
        return {
          isValid: true,
          status: 'valid',
          message: 'Campaign is active and ready to play!',
          campaign: validation.campaign,
          shortCode
        };
      }

      // Handle different invalid states
      switch (validation.reason) {
        case 'not_found':
          return {
            isValid: false,
            status: 'invalid_short_code',
            message: `The campaign code "${shortCode}" was not found.`,
            shortCode,
            suggestedActions: [
              'Double-check the QR code or link',
              'Ask for a current link from the venue',
              'This code may be from an old promotion'
            ]
          };

        case 'expired':
          return {
            isValid: false,
            status: 'expired',
            message: `This campaign ended on ${validation.endDate ? new Date(validation.endDate).toLocaleDateString() : 'a previous date'}.`,
            campaign: validation.campaign,
            shortCode,
            suggestedActions: [
              'Ask about current promotions at the venue',
              'Check for new QR codes or links',
              'Contact the venue for active campaigns'
            ]
          };

        case 'not_started':
          return {
            isValid: false,
            status: 'not_started',
            message: `This campaign starts on ${validation.startDate ? new Date(validation.startDate).toLocaleDateString() : 'a future date'}.`,
            campaign: validation.campaign,
            shortCode,
            suggestedActions: [
              'Save this link and try again after the start date',
              'Check with the venue for current promotions',
              'Sign up for notifications when this campaign begins'
            ]
          };

        case 'inactive':
          return {
            isValid: false,
            status: 'inactive',
            message: 'This campaign is currently disabled.',
            campaign: validation.campaign,
            shortCode,
            suggestedActions: [
              'Contact the venue for assistance',
              'Ask about alternative promotions',
              'Check back later as this may be temporary'
            ]
          };

        default:
          return {
            isValid: false,
            status: 'error',
            message: 'Unable to validate this campaign. Please try again.',
            shortCode,
            suggestedActions: [
              'Try refreshing the page',
              'Check your internet connection',
              'Contact support if the issue persists'
            ]
          };
      }

    } catch (error) {
      console.error('Campaign validation error:', error);
      return {
        isValid: false,
        status: 'error',
        message: 'Unable to check campaign status. Please try again.',
        shortCode,
        suggestedActions: [
          'Check your internet connection',
          'Try refreshing the page',
          'Contact support if the issue continues'
        ]
      };
    }
  }

  async getCampaignInfo(shortCode: string): Promise<CampaignInfo | null> {
    try {
      const getCampaign = httpsCallable(this.firebaseService.functions, 'getCampaign');
      const result = await getCampaign({ shortCode });
      return result.data as CampaignInfo;
    } catch (error) {
      console.error('Error getting campaign info:', error);
      return null;
    }
  }
}
